/**
 * SIL-1351 - My Activity copy. Spec:
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md section 4.6,
 * wording from docs/exchange/design-handoff/README.md section 7 and the
 * `activitySections` builder in the handoff prototype
 * (docs/exchange/design-handoff/SiliconX Exchange.dc.html).
 *
 * Same discipline as `activeVerification.ts` and `buyerVerification.ts`: the
 * sentences live here, not in the components. Three of these strings appear on
 * two routes at once (`/offers` and `/transactions` both render `MyActivity`
 * for a buyer), so an inline copy would be two copies from the day it shipped.
 */

/** Page header. */
export const MY_ACTIVITY_TITLE = 'My Activity';

/**
 * The handoff sentence is "Orders, offers and expressed interest across the
 * exchange." Expressed interest is hidden until SIL-1319 ships the underlying
 * signal (slice 4), and a subtitle promising a card that is not on the page
 * reads as a rendering bug. Restore the handoff wording with the card.
 */
export const MY_ACTIVITY_SUBTITLE = 'Orders and offers across the exchange.';

/** One card's copy: heading, one-line description and the empty state. */
export interface ActivityCardCopy {
  readonly title: string;
  readonly description: string;
  readonly emptyTitle: string;
  readonly emptyBody: string;
}

export const ORDERS_CARD: ActivityCardCopy = {
  title: 'Orders',
  description: 'Purchase requests and accepted deals.',
  emptyTitle: 'No orders yet',
  emptyBody: 'Orders appear here after a Buy now request or an accepted offer.',
};

export const OFFERS_CARD: ActivityCardCopy = {
  title: 'Offers',
  description: 'Price proposals you have made on listings.',
  emptyTitle: 'No offers yet',
  emptyBody: 'Make an offer from any listing.',
};

/**
 * Built and tested, deliberately not rendered - `MyActivity` keeps the card
 * out of the tree until SIL-1319 (slice 4) provides expressed-interest rows.
 */
export const EXPRESSED_INTEREST_CARD: ActivityCardCopy = {
  title: 'Expressed interest',
  description: 'Listings where you shared your workload with the seller.',
  emptyTitle: 'No expressed interest yet',
  emptyBody: 'Listings you express interest in appear here.',
};

/**
 * Countered-offer row. The amount is appended by `counteredAtLabel` rather
 * than interpolated at the call site, so the badge and any future toast can
 * never spell the same fact two ways.
 */
export const COUNTERED_AT_PREFIX = 'Countered at ';

export function counteredAtLabel(formattedAmount: string): string {
  return `${COUNTERED_AT_PREFIX}${formattedAmount}`;
}

export const COUNTER_ACCEPT_LABEL = 'Accept';
export const COUNTER_DECLINE_LABEL = 'Decline';
export const COUNTER_ACCEPT_PENDING_LABEL = 'Accepting…';
export const COUNTER_DECLINE_PENDING_LABEL = 'Declining…';

export const COUNTER_ACCEPTED_TOAST = 'Counter accepted.';
export const COUNTER_DECLINED_TOAST = 'Counter declined.';

/** Fallbacks handed to `exchangeErrorMessage` so a codeless failure still names the action. */
export const COUNTER_ACCEPT_ERROR_FALLBACK = 'We could not accept that counter. Try again.';
export const COUNTER_DECLINE_ERROR_FALLBACK = 'We could not decline that counter. Try again.';

/**
 * Order (marketplace_transaction) lifecycle states, in the buyer's words.
 * The four keys are the CHECK constraint from migration 477 - a state outside
 * them falls back to the raw token rather than rendering nothing, so a future
 * state is visible rather than silently blank.
 */
export const ORDER_STATUS_LABELS: Record<string, string> = {
  pending_seller_approval: 'Awaiting seller',
  seller_approved: 'Accepted',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export function orderStatusLabel(lifecycleState: string): string {
  return ORDER_STATUS_LABELS[lifecycleState] ?? lifecycleState;
}

/** Row field labels - screen-reader names for the unlabelled grid cells. */
export const ROW_AMOUNT_LABEL = 'Amount';
export const ROW_DATE_LABEL = 'Date';
export const ROW_ID_LABEL = 'Reference';
